import isNil from 'lodash/isNil';
import moment, {Moment} from 'moment';
import Time, {validate as validateTime} from './Time';
import effectiveEndDate from 'utils/effectiveEndDate';
import repetitionCount from 'utils/repetitionCount';
import {List} from 'immutable';
import {requiredMessage, greaterThanMessage} from 'utils/const';
import {Data, Int, RecordProps} from '@store/@types/util';

export function validate(schedule: EventSchedule): any {
  // todo type maybe
  const errors: any = {};
  ['weeksBetween', 'time', 'durationInMinutes'].forEach(prop => {
    if ((schedule[prop] || '') === '') {
      errors[prop] = requiredMessage;
    }
  });
  if (!schedule.daysOfWeek || schedule.daysOfWeek.size === 0) {
    errors.daysOfWeek = requiredMessage;
  }
  if (schedule.weeksBetween < 0) {
    errors.weeksBetween = greaterThanMessage(0);
  }
  if (schedule.weeksBetween >= 10) {
    errors.weeksBetween = 'Must be less than 10';
  }
  if (!isNil(schedule.recurrenceCount) && schedule.recurrenceCount <= 0) {
    errors.recurrenceCount = greaterThanMessage(0);
  }
  if (schedule.time) {
    const timeErrors = validateTime(schedule.time);
    if (Object.keys(timeErrors).length > 0) {
      errors.time = timeErrors;
    }
  }

  const recurrenceEndType = schedule.recurrenceEndType;

  if (recurrenceEndType === RECURRENCE_COUNT_TYPE && !schedule.recurrenceCount) {
    errors.recurrenceCount = requiredMessage;
  }

  if (recurrenceEndType === RECURRENCE_END_DATE_TYPE && !schedule.recurrenceEndDate) {
    errors.recurrenceEndDate = requiredMessage;
  }

  return errors;
}

export const RECURRENCE_COUNT_TYPE = 'recurrenceCount';
export const RECURRENCE_END_DATE_TYPE = 'recurrenceEndDate';
export const RECURRENCE_NO_END_TYPE = 'never';
type RecurrenceType =
  | typeof RECURRENCE_COUNT_TYPE
  | typeof RECURRENCE_END_DATE_TYPE
  | typeof RECURRENCE_NO_END_TYPE;

export type ScheduleId = unknown;
interface EventScheduleInterface {
  id: ScheduleId;
  weeksBetween: Int;
  time: Time;
  daysOfWeek: List<string>;
  recurrenceStartDate: Moment;
  recurrenceCount: Int;
  recurrenceEndDate: Moment | null;
  // entirely synthetic - used to drive validation on create plan modal.
  recurrenceEndType: RecurrenceType | Moment;
  durationInMinutes: Int;
}

export default class EventSchedule extends Data<EventScheduleInterface>({
  id: undefined,
  weeksBetween: undefined,
  time: undefined,
  daysOfWeek: List(),
  recurrenceStartDate: moment(),
  recurrenceCount: undefined,
  recurrenceEndDate: undefined,
  // entirely synthetic - used to drive validation on create plan modal.
  recurrenceEndType: undefined,
  durationInMinutes: undefined,
}) {
  constructor(object: RecordProps<EventScheduleInterface> = {}) {
    const uncasedDaysOfWeek =
      object.daysOfWeek &&
      object.daysOfWeek.map(day => day[0].toUpperCase() + day.slice(1).toLowerCase());
    super({
      ...object,
      daysOfWeek: List(uncasedDaysOfWeek || []),
      recurrenceEndDate: object.recurrenceEndDate ? moment(object.recurrenceEndDate) : null,
      time: new Time(object.time),
      recurrenceEndType: object.recurrenceCount
        ? RECURRENCE_COUNT_TYPE
        : object.recurrenceEndDate
        ? RECURRENCE_END_DATE_TYPE
        : RECURRENCE_NO_END_TYPE,
    });
  }

  getEffectiveEndDate(): Moment | undefined {
    return effectiveEndDate(this, this.recurrenceStartDate);
  }

  getRepetitionCount(): Int | null | typeof Infinity {
    return repetitionCount(this, this.recurrenceStartDate);
  }

  getPeriod(): null | {unit: 'week'; quantity: Int} {
    const endDate = this.getEffectiveEndDate();
    if (!endDate) {
      return null;
    }
    return {
      unit: 'week',
      quantity: Math.ceil(endDate.diff(this.recurrenceStartDate, 'day') / 7),
    };
  }
}
